import React, { useRef } from 'react';
import { Chip, IconButton, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

// Must match the backend (EmpowerSales_APP/attachments.py)
export const ATTACHMENT_ACCEPT = '.csv,.tsv,.xlsx,.xlsm,.json,.txt,.md,.tf,.tfvars,.yaml,.yml,.xml,.log,.sql,.pdf';
export const MAX_ATTACHMENT_MB = 10;

export const DEFAULT_ATTACHMENT_PROMPTS = {
  review: 'Review the attached file and suggest enhancements.',
  terraform: 'Generate Terraform for the resources in the attached file.',
};

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Reads a File into { name, size, data } (base64), or throws a user-facing Error
export const loadAttachment = async (file) => {
  const extension = `.${file.name.split('.').pop().toLowerCase()}`;
  if (!ATTACHMENT_ACCEPT.split(',').includes(extension)) {
    throw new Error(extension === '.xls'
      ? 'Old .xls files are not supported. Please save the file as .xlsx or CSV.'
      : 'Unsupported file type. Use CSV, XLSX, JSON, PDF or a text file.');
  }
  if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
    throw new Error(`The file is larger than ${MAX_ATTACHMENT_MB} MB.`);
  }
  return { name: file.name, size: file.size, data: await readAsBase64(file) };
};

const formatSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

export const AttachButton = ({ onAttach, onError, disabled }) => {
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow picking the same file again
    if (!file) return;
    try {
      onAttach(await loadAttachment(file));
    } catch (error) {
      onError(error.message || 'The file could not be read.');
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept={ATTACHMENT_ACCEPT} hidden onChange={handleChange} />
      <Tooltip title="Attach a file (CMDB export, inventory CSV/XLSX, JSON, PDF, config)" arrow describeChild>
        <span>
          <IconButton size="small" aria-label="Attach file" disabled={disabled} onClick={() => inputRef.current?.click()}>
            <AttachFileIcon sx={{ fontSize: 22, color: '#6b6a85', transform: 'rotate(45deg)' }} />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};

export const AttachmentChip = ({ attachment, onRemove }) => (
  <Chip
    icon={<InsertDriveFileOutlinedIcon />}
    label={attachment.size ? `${attachment.name} (${formatSize(attachment.size)})` : attachment.name}
    onDelete={onRemove}
    size="small"
    sx={{ fontFamily: 'Kumbh Sans', fontSize: '13.5px', fontWeight: 600, color: '#1a0652', backgroundColor: '#f1eefe', border: '1px solid #e3dcfd', height: 30, maxWidth: '100%', '& .MuiChip-icon': { color: '#6a4cff' } }}
  />
);
